import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { TanTable } from '../../../components/TanTable';
import Notification, { NotificationContainer } from '../../../components/Notification';
import thirdPartiesService from '../../../services/compliance/thirdParties.service';
import contractsService from '../../../services/compliance/contracts.service';
import tprmService from '../../../services/compliance/tprm.service';
import { Contract, ThirdParty, Vendor } from '../../../types/compliance';
import { effectiveStatus } from '../contracts/contractTerm';
import ThirdPartyDrawer from './ThirdPartyDrawer';
import ThirdPartyBulkImportDrawer from './ThirdPartyBulkImportDrawer';

const TIER_BADGE: Record<string, string> = {
  low: 'bg-green-200 text-green-800 dark:bg-green-900 dark:text-green-200',
  medium: 'bg-blue-200 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  high: 'bg-amber-200 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  critical: 'bg-red-200 text-red-800 dark:bg-red-900 dark:text-red-200',
};

const CONTRACT_STATUS_BADGE: Record<string, string> = {
  draft: 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  active: 'bg-green-200 text-green-800 dark:bg-green-900 dark:text-green-200',
  pending_renewal: 'bg-yellow-200 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  expired: 'bg-red-200 text-red-800 dark:bg-red-900 dark:text-red-200',
  terminated: 'bg-red-200 text-red-800 dark:bg-red-900 dark:text-red-200',
};

const VENDOR_STATUS_BADGE: Record<string, string> = {
  draft: 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  submitted: 'bg-blue-200 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  under_review: 'bg-indigo-200 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
  approved: 'bg-green-200 text-green-800 dark:bg-green-900 dark:text-green-200',
  approved_with_conditions: 'bg-amber-200 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  rejected: 'bg-red-200 text-red-800 dark:bg-red-900 dark:text-red-200',
  archived: 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
};

function fmtShortDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}

function fmtCurrency(value: string | null | undefined, currency: string): string {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return value as string;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `${currency} ${n.toLocaleString()}`;
  }
}

type Props = {
  embedded?: boolean;
};


export default function ThirdPartyList({ embedded = false }: Props = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<ThirdParty[]>([]);
  // Children loaded eagerly so the expanded panel renders instantly; both
  // datasets are small (< few thousand) in practice. Indexed by third_party_id.
  const [contractsByTpId, setContractsByTpId] = useState<Record<string, Contract[]>>({});
  const [vendorsByTpId, setVendorsByTpId] = useState<Record<string, Vendor[]>>({});
  const [loading, setLoading] = useState(true);
  const [errorNotification, setErrorNotification] = useState<{ title: string; message: string } | null>(null);
  const [successNotification, setSuccessNotification] = useState<{ title: string; message: string } | null>(null);
  const [drawer, setDrawer] = useState<{
    open: boolean;
    mode: 'create' | 'edit';
    id: string | null;
  }>({ open: false, mode: 'create', id: null });
  const [bulkOpen, setBulkOpen] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [tps, contracts, vendors] = await Promise.all([
        thirdPartiesService.list(),
        // Children are best-effort — a 500 on contracts shouldn't prevent
        // the Third Parties table from rendering. Catch + fall back to [].
        contractsService.list().catch(() => [] as Contract[]),
        tprmService.listVendors().catch(() => [] as Vendor[]),
      ]);
      setData(tps);

      const cMap: Record<string, Contract[]> = {};
      contracts.forEach((c) => {
        if (!c.third_party_id) return;
        (cMap[c.third_party_id] ||= []).push(c);
      });
      // Newest contract first inside each group.
      Object.values(cMap).forEach((list) =>
        list.sort((a, b) => (a.created_at < b.created_at ? 1 : -1)),
      );
      setContractsByTpId(cMap);

      const vMap: Record<string, Vendor[]> = {};
      vendors.forEach((v) => {
        if (!v.third_party_id) return;
        (vMap[v.third_party_id] ||= []).push(v);
      });
      Object.values(vMap).forEach((list) =>
        list.sort((a, b) => (a.name || '').localeCompare(b.name || '')),
      );
      setVendorsByTpId(vMap);
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
      setErrorNotification({ title: 'Failed to load third parties', message: msg });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cross-link restoration: /compliance/third-parties?id=<id> from audit log.
  useEffect(() => {
    const id = searchParams.get('id');
    if (id) {
      setDrawer({ open: true, mode: 'edit', id });
      searchParams.delete('id');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleDelete = useCallback(
    async (row: ThirdParty) => {
      if (!window.confirm(`Are you sure you want to delete "${row.name}"?`)) return;
      try {
        await thirdPartiesService.remove(row.id);
        setSuccessNotification({
          title: 'Deleted Successfully',
          message: `Third party "${row.name}" has been deleted.`,
        });
        loadData();
      } catch (error: any) {
        const msg = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
        setErrorNotification({ title: 'Failed to Delete', message: msg });
      }
    },
    [loadData],
  );

  const formatDate = (iso: string | null | undefined) => {
    if (!iso) return '-';
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  const renderExpandedContent = useCallback(
    (tp: ThirdParty) => {
      const contracts = contractsByTpId[tp.id] || [];
      const vendors = vendorsByTpId[tp.id] || [];
      return (
        <div className="px-6 py-4 space-y-4 bg-gray-50 dark:bg-gray-800/60">
          {/* Contracts */}
          <section>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300 mb-2">
              Contracts ({contracts.length})
            </h4>
            {contracts.length === 0 ? (
              <p className="text-xs text-muted italic">
                No contracts on file.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-muted border-b border-gray-200 dark:border-gray-700">
                      <th className="py-1 pr-4 font-medium">Title</th>
                      <th className="py-1 pr-4 font-medium">Category</th>
                      <th className="py-1 pr-4 font-medium">Status</th>
                      <th className="py-1 pr-4 font-medium">Start</th>
                      <th className="py-1 pr-4 font-medium">End</th>
                      <th className="py-1 pr-4 font-medium text-right">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contracts.map((c) => (
                      <tr
                        key={c.id}
                        className="border-b border-gray-100 dark:border-gray-800 last:border-b-0"
                      >
                        <td className="py-1 pr-4">
                          <Link
                            to={`/compliance/contracts?id=${c.id}`}
                            className="text-blue-600 dark:text-blue-400 hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {c.title}
                          </Link>
                        </td>
                        <td className="py-1 pr-4 text-label">
                          {c.category || '—'}
                        </td>
                        <td className="py-1 pr-4">
                          <span
                            className={`px-2 py-0.5 text-xs font-medium rounded ${
                              CONTRACT_STATUS_BADGE[effectiveStatus(c)] || ''
                            }`}
                          >
                            {effectiveStatus(c).replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="py-1 pr-4 text-label">
                          {fmtShortDate(c.start_date)}
                        </td>
                        <td className="py-1 pr-4 text-label">
                          {fmtShortDate(c.end_date)}
                        </td>
                        <td className="py-1 pr-4 text-right text-label">
                          {fmtCurrency(c.contract_value, c.currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Vendor assessments */}
          <section>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300 mb-2">
              Vendor Assessments ({vendors.length})
            </h4>
            {vendors.length === 0 ? (
              <p className="text-xs text-muted italic">
                No vendor assessments on file.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-muted border-b border-gray-200 dark:border-gray-700">
                      <th className="py-1 pr-4 font-medium">Vendor</th>
                      <th className="py-1 pr-4 font-medium">Service Type</th>
                      <th className="py-1 pr-4 font-medium">Status</th>
                      <th className="py-1 pr-4 font-medium">Tier</th>
                      <th className="py-1 pr-4 font-medium text-right">Score</th>
                      <th className="py-1 pr-4 font-medium">Next Review</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vendors.map((v) => {
                      const tier = v.scores?.risk_tier || '';
                      const score = v.scores?.total_score;
                      return (
                        <tr
                          key={v.id}
                          className="border-b border-gray-100 dark:border-gray-800 last:border-b-0"
                        >
                          <td className="py-1 pr-4">
                            <Link
                              to={`/compliance/tprm/vendors/${v.id}`}
                              className="text-blue-600 dark:text-blue-400 hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {v.name}
                            </Link>
                            {v.product_service_name && (
                              <div className="text-xs text-muted">
                                {v.product_service_name}
                              </div>
                            )}
                          </td>
                          <td className="py-1 pr-4 text-label">
                            {v.service_type || '—'}
                          </td>
                          <td className="py-1 pr-4">
                            <span
                              className={`px-2 py-0.5 text-xs font-medium rounded ${
                                VENDOR_STATUS_BADGE[v.status] || ''
                              }`}
                            >
                              {v.status.replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td className="py-1 pr-4">
                            {tier ? (
                              <span
                                className={`px-2 py-0.5 text-xs font-medium rounded ${
                                  TIER_BADGE[tier] || ''
                                }`}
                              >
                                {tier}
                              </span>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="py-1 pr-4 text-right text-label">
                            {score != null ? score.toFixed(2) : '—'}
                          </td>
                          <td className="py-1 pr-4 text-label">
                            {fmtShortDate(v.next_review_date)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      );
    },
    [contractsByTpId, vendorsByTpId],
  );

  const columns = useMemo<ColumnDef<ThirdParty>[]>(
    () => [
      {
        accessorKey: 'name',
        header: () => <span className="pl-3">Name</span>,
        enableSorting: true,
        size: 220,
        cell: ({ row }) => (
          <span className="pl-3 font-semibold text-blue-600 dark:text-blue-400">{row.original.name}</span>
        ),
      },
      {
        accessorKey: 'legal_entity_name',
        header: 'Legal Entity',
        enableSorting: true,
        size: 220,
        cell: ({ row }) => <span>{row.original.legal_entity_name || '-'}</span>,
      },
      {
        accessorKey: 'website',
        header: 'Website',
        enableSorting: false,
        size: 240,
        cell: ({ row }) => {
          const w = row.original.website;
          if (!w) return <span className="text-gray-400">-</span>;
          return (
            <a
              href={w}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline dark:text-blue-400"
              onClick={(e) => e.stopPropagation()}
            >
              {w}
            </a>
          );
        },
      },
      {
        accessorKey: 'created_at',
        header: 'Created At',
        enableSorting: true,
        size: 180,
        cell: ({ row }) => <span>{formatDate(row.original.created_at)}</span>,
      },
      {
        id: 'actions',
        header: 'Actions',
        size: 100,
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => (
          <div className="flex gap-2 justify-end">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setDrawer({ open: true, mode: 'edit', id: row.original.id });
              }}
              className="p-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
              title="Edit"
            >
              <PencilIcon className="w-4 h-4" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(row.original);
              }}
              className="p-1 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 transition-colors"
              title="Delete"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          </div>
        ),
      },
    ],
    [handleDelete],
  );

  return (
    <div className="h-full w-full p-6 pb-24">
      {!embedded && (
        <div className="mb-4">
          <h1 className="text-2xl font-bold">Third Parties</h1>
          <p className="text-sm text-muted">
            Organizations (vendors, customers, partners) referenced from compliance entities.
          </p>
        </div>
      )}

      <TanTable
        data={data}
        columns={columns}
        loading={loading}
        settingsKey="compliance-third-parties-table"
        fieldName="compliance-third-parties-table"
        searchPlaceholder="Search third parties..."
        enableGrouping={false}
        enableColumnFilters={true}
        cardTitle="Third Parties"
        lockedColumns={[{ id: 'actions', side: 'right' }]}
        showEditFiltersButton={true}
        editFiltersButtonIcon="plus"
        editFiltersButtonTooltip="New Third Party"
        onEditFilters={() => setDrawer({ open: true, mode: 'create', id: null })}
        renderExpandedContent={renderExpandedContent}
      />

      <ThirdPartyDrawer
        mode={drawer.mode}
        thirdPartyId={drawer.id}
        open={drawer.open}
        onClose={() => setDrawer((d) => ({ ...d, open: false }))}
        onSaved={() => {
          loadData();
        }}
        onDeleted={() => {
          setDrawer((d) => ({ ...d, open: false }));
          loadData();
        }}
        onBulkImport={() => {
          // Close the create drawer, open the bulk-import panel on the left.
          setDrawer((d) => ({ ...d, open: false }));
          setBulkOpen(true);
        }}
      />

      <ThirdPartyBulkImportDrawer
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onImported={loadData}
      />

      {errorNotification && (
        <NotificationContainer position="top-right">
          <Notification
            type="error"
            title={errorNotification.title}
            message={errorNotification.message}
            showClose={true}
            onClose={() => setErrorNotification(null)}
            autoDismiss={5000}
          />
        </NotificationContainer>
      )}

      {successNotification && (
        <NotificationContainer position="top-right">
          <Notification
            type="success"
            title={successNotification.title}
            message={successNotification.message}
            showClose={true}
            onClose={() => setSuccessNotification(null)}
            autoDismiss={3000}
          />
        </NotificationContainer>
      )}
    </div>
  );
}
